import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, RefreshCw } from 'lucide-react';
import {
  CategoryIcon,
  CATEGORY_COLORS,
  EMOJI_SECTIONS,
  LUCIDE_SECTIONS,
  FA_SECTIONS,
  FA_MAP,
  MD_SECTIONS,
  MD_MAP,
  BS_SECTIONS,
  BS_MAP,
  ReactIconComp,
} from '../../../pages/Inventory.tsx';
import apiClient from '../../../api/client.ts';
import * as LucideIcons from 'lucide-react';

type IconSuggestion = { name: string; label: string };
type LibTab = 'lucide' | 'emoji' | 'fa' | 'md' | 'bs';

const NEUTRAL_COLOR = '#94A3B8';

const POPOVER_WIDTH = 440;
const POPOVER_MAX_HEIGHT = 500;
const GAP = 8;
const VIEWPORT_MARGIN = 16;
// Modal is .modal-overlay, z-index 1000 (global.css) -- this popover must paint above the whole
// modal (Fix 1: it used to be position:absolute inside the editor's own scrolling content div,
// so the scroll container clipped it and the footer painted over it on top of that).
const POPOVER_Z = 1010;

export function IconColorPopover({
  anchorRef,
  categoryName,
  currentIconPath,
  currentColor,
  isNested,
  parentColor,
  readOnly,
  onPickIcon,
  onPickColor,
  onClear,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  categoryName: string;
  currentIconPath: string | null;
  currentColor: string;
  isNested: boolean;
  parentColor?: string;
  readOnly: boolean;
  onPickIcon: (iconPath: string) => void;
  onPickColor: (hex: string) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<LibTab>('lucide');
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);

  // Fix 1: compute fixed-viewport coordinates from the tile's own getBoundingClientRect, flipping
  // above the tile when there isn't ~320px of room below (and clamping width/height to the
  // viewport either way), instead of CSS position:absolute relative to a scrolling ancestor.
  useLayoutEffect(() => {
    function recompute() {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
      const left = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN);
      if (spaceBelow >= 320 || spaceBelow >= spaceAbove) {
        setPos({ left, top: rect.bottom + GAP, maxHeight: Math.min(POPOVER_MAX_HEIGHT, Math.max(spaceBelow, 160)) });
      } else {
        setPos({ left, bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(POPOVER_MAX_HEIGHT, Math.max(spaceAbove, 160)) });
      }
    }
    recompute();
    window.addEventListener('resize', recompute);
    // capture:true so this also fires for scroll events on the editor's own nested scroll
    // container (CategoryEditor's overflowY:auto content div), not just window-level scrolling.
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [anchorRef]);

  const [keywordSuggestions, setKeywordSuggestions] = useState<IconSuggestion[]>([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<IconSuggestion[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(false);

  async function fetchKeyword() {
    if (!categoryName.trim()) { setKeywordSuggestions([]); return; }
    setKeywordLoading(true);
    try {
      const res = await apiClient.post<{ suggestions: IconSuggestion[] }>('/inventory/suggest-icons', { categoryName: categoryName.trim() });
      setKeywordSuggestions(res.data.suggestions ?? []);
    } catch {
      setKeywordSuggestions([]);
    } finally {
      setKeywordLoading(false);
    }
  }

  async function fetchAi(existing: string[] = []) {
    if (!categoryName.trim()) { setAiSuggestions([]); return; }
    setAiLoading(true);
    setAiError(false);
    try {
      // TODO: /inventory/suggest-icons-ai has no orgPlanTier/admin gate today (confirmed by
      // reading the route) -- every role that can open this popover at all can trigger a Claude
      // call. Flagged, not fixed -- out of scope for this round per the task.
      const res = await apiClient.post<{ suggestions: IconSuggestion[] }>('/inventory/suggest-icons-ai', { categoryName: categoryName.trim(), existing });
      setAiSuggestions(res.data.suggestions ?? []);
    } catch {
      setAiSuggestions([]);
      setAiError(true);
    } finally {
      setAiLoading(false);
    }
  }

  // Part 5: both suggestion rows fetch once when the popover opens, keyed off the category's
  // CURRENT name -- not on every keystroke in the editor's name field anymore (that live-typing
  // auto-fire was Inventory.tsx's old CategoryManager behavior; this popover is a separate,
  // explicit "pick an icon" step now).
  useEffect(() => {
    void fetchKeyword();
    void fetchAi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus trap + Esc/outside-click close + return focus to whatever was focused before the
  // popover opened (the icon tile, since that's what's clicked to open it).
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const focusable = containerRef.current.querySelectorAll<HTMLElement>(
        'button, input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insidePopover = containerRef.current?.contains(target);
      const insideAnchor = anchorRef.current?.contains(target);
      if (!insidePopover && !insideAnchor) onClose();
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleOutside);
    containerRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleOutside);
      previouslyFocused?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickIcon(name: string) {
    if (readOnly) return;
    onPickIcon(name);
    onClose();
  }

  const extraSwatch = !CATEGORY_COLORS.includes(currentColor) && currentColor !== NEUTRAL_COLOR ? currentColor : null;

  const gridButtonStyle = (isSel: boolean): React.CSSProperties => ({
    width: 40, height: 40, borderRadius: 8, flexShrink: 0, padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `2px solid ${isSel ? currentColor : 'var(--border-default)'}`,
    background: isSel ? `${currentColor}22` : 'var(--bg-elevated)',
    color: isSel ? currentColor : 'var(--text-secondary)',
    cursor: readOnly ? 'default' : 'pointer',
  });

  if (!pos) return null; // not measured yet -- avoids a one-frame flash at the wrong spot

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Icon and color"
      className="cat-popover-fade"
      style={{
        position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, zIndex: POPOVER_Z,
        width: POPOVER_WIDTH, maxHeight: pos.maxHeight, overflowY: 'auto',
        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
        borderRadius: 14, boxShadow: '0 20px 48px rgba(0,0,0,0.45)',
        padding: 16, display: 'flex', flexDirection: 'column', gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Icon &amp; color</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!readOnly && (
            <button type="button" onClick={() => { onClear(); onClose(); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: 0 }}>
              Clear
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 2 }}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Section 1: AI picks */}
      <div style={{
        borderRadius: 10, padding: 12,
        background: 'linear-gradient(135deg, rgba(139,92,246,.16), rgba(59,130,246,.08))',
        border: '1px solid rgba(167,139,250,.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa' }}>✦ AI picks for &ldquo;{categoryName}&rdquo;</span>
          <button
            type="button"
            onClick={() => void fetchAi(aiSuggestions.map(s => s.name))}
            title="Refresh"
            aria-label="Refresh AI suggestions"
            className="cat-popover-refresh"
            style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', display: 'flex', padding: 2 }}
          >
            <RefreshCw size={13} />
          </button>
        </div>
        {aiLoading ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cat-popover-shimmer" style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(255,255,255,0.08)', animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        ) : aiError ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>AI suggestions unavailable right now.</div>
        ) : aiSuggestions.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No AI suggestions yet.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {aiSuggestions.map((s) => (
              <button key={s.name} type="button" title={s.name} disabled={readOnly} onClick={() => pickIcon(s.name)} style={gridButtonStyle(currentIconPath === s.name)}>
                <CategoryIcon iconName={s.name} size={18} color="currentColor" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: keyword suggestions */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Suggested</div>
        {keywordLoading ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cat-popover-shimmer" style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(255,255,255,0.08)', animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywordSuggestions.slice(0, 10).map((s) => (
              <button key={s.name} type="button" title={s.name} disabled={readOnly} onClick={() => pickIcon(s.name)} style={gridButtonStyle(currentIconPath === s.name)}>
                <CategoryIcon iconName={s.name} size={18} color="currentColor" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Section 3: browse all */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Browse all</div>
        <input
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%', padding: '6px 10px', marginBottom: 8, boxSizing: 'border-box',
            background: 'var(--bg-base)', border: '1px solid var(--border-default)',
            borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
          {([
            { id: 'lucide', label: 'Lucide' },
            { id: 'emoji', label: 'Emoji' },
            { id: 'fa', label: 'FA' },
            { id: 'md', label: 'Material' },
            { id: 'bs', label: 'Bootstrap' },
          ] as const).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                border: `1px solid ${tab === t.id ? 'var(--accent-primary)' : 'var(--border-default)'}`,
                background: tab === t.id ? 'rgba(59,130,246,0.12)' : 'transparent',
                color: tab === t.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ maxHeight: 180, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4 }}>
          {tab === 'lucide' && LUCIDE_SECTIONS.flatMap((sec) => sec.icons)
            .filter((n) => !search || n.toLowerCase().includes(search.toLowerCase()))
            .map((iconName) => {
              const IconComp = (LucideIcons as unknown as Record<string, ReactIconComp>)[iconName];
              if (!IconComp) return null;
              const isSel = currentIconPath === iconName;
              return (
                <button key={iconName} type="button" title={iconName} disabled={readOnly} onClick={() => pickIcon(iconName)} style={gridButtonStyle(isSel)}>
                  <IconComp size={18} color="currentColor" />
                </button>
              );
            })}
          {tab === 'emoji' && EMOJI_SECTIONS.flatMap((sec) => sec.emojis)
            .filter((e) => !search || e.includes(search))
            .map((emoji) => {
              const isSel = currentIconPath === emoji;
              return (
                <button key={emoji} type="button" title={emoji} disabled={readOnly} onClick={() => pickIcon(emoji)} style={{ ...gridButtonStyle(isSel), fontSize: 18 }}>
                  {emoji}
                </button>
              );
            })}
          {tab === 'fa' && FA_SECTIONS.flatMap((sec) => sec.icons)
            .filter((n) => !search || n.toLowerCase().includes(search.toLowerCase()))
            .map((iconName) => {
              const IconComp = FA_MAP[iconName];
              if (!IconComp) return null;
              const stored = `fa:${iconName}`;
              const isSel = currentIconPath === stored;
              return (
                <button key={iconName} type="button" title={iconName} disabled={readOnly} onClick={() => pickIcon(stored)} style={gridButtonStyle(isSel)}>
                  <IconComp size={18} color="currentColor" />
                </button>
              );
            })}
          {tab === 'md' && MD_SECTIONS.flatMap((sec) => sec.icons)
            .filter((n) => !search || n.toLowerCase().includes(search.toLowerCase()))
            .map((iconName) => {
              const IconComp = MD_MAP[iconName];
              if (!IconComp) return null;
              const stored = `md:${iconName}`;
              const isSel = currentIconPath === stored;
              return (
                <button key={iconName} type="button" title={iconName} disabled={readOnly} onClick={() => pickIcon(stored)} style={gridButtonStyle(isSel)}>
                  <IconComp size={18} color="currentColor" />
                </button>
              );
            })}
          {tab === 'bs' && BS_SECTIONS.flatMap((sec) => sec.icons)
            .filter((n) => !search || n.toLowerCase().includes(search.toLowerCase()))
            .map((iconName) => {
              const IconComp = BS_MAP[iconName];
              if (!IconComp) return null;
              const stored = `bs:${iconName}`;
              const isSel = currentIconPath === stored;
              return (
                <button key={iconName} type="button" title={iconName} disabled={readOnly} onClick={() => pickIcon(stored)} style={gridButtonStyle(isSel)}>
                  <IconComp size={18} color="currentColor" />
                </button>
              );
            })}
        </div>
      </div>

      {/* Section 4: color */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Color</span>
          {isNested && parentColor && !readOnly && (
            <button
              type="button"
              className="btn btn-ghost btn-sm combo1"
              onClick={() => onPickColor(parentColor)}
              style={{ fontSize: 11, padding: '3px 8px' }}
            >
              Match parent
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[...CATEGORY_COLORS, NEUTRAL_COLOR, ...(extraSwatch ? [extraSwatch] : [])].map((hex, i) => {
            const isSel = currentColor === hex;
            const isExtra = extraSwatch && hex === extraSwatch;
            return (
              <button
                key={`${hex}-${i}`}
                type="button"
                title={isExtra ? `Current (${hex})` : hex}
                disabled={readOnly}
                onClick={() => !readOnly && onPickColor(hex)}
                style={{
                  width: 28, height: 28, borderRadius: '50%', background: hex, flexShrink: 0,
                  border: 'none', cursor: readOnly ? 'default' : 'pointer',
                  boxShadow: isSel ? `0 0 0 2px var(--bg-elevated), 0 0 0 4px ${hex}` : 'none',
                }}
              />
            );
          })}
        </div>
      </div>

      <style>{`
        .cat-popover-fade { animation: catPopoverFadeIn 0.15s ease; }
        @keyframes catPopoverFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .cat-popover-refresh { transition: transform 0.2s ease; }
        .cat-popover-refresh:hover { transform: rotate(90deg); }
        @keyframes catPopoverShimmer { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.9; } }
        .cat-popover-shimmer { animation: catPopoverShimmer 1.3s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .cat-popover-fade { animation: none; }
          .cat-popover-refresh { transition: none; }
          .cat-popover-refresh:hover { transform: none; }
          .cat-popover-shimmer { animation: none; opacity: 0.6; }
        }
      `}</style>
    </div>,
    document.body
  );
}
